/**
 * eSign SDK
 *
 * A lightweight JavaScript SDK for integrating eSignatures.com e-signatures into static sites.
 * Reads configuration from HTML data attributes and handles contract creation via the backend API.
 *
 * Usage:
 *   <button class="esign-trigger"
 *           data-template-id="template_abc123"
 *           data-document-name="Client Agreement"
 *           data-signer-email="client@example.com"
 *           data-signer-name="John Doe"
 *           data-redirect-url="/thank-you">
 *     Sign Agreement
 *   </button>
 *
 *   <script src="/static/esign-sdk.js"></script>
 */

const ESignSDK = (function() {
  'use strict';

  // Default configuration
  const config = {
    apiEndpoint: '/api/esign/create-contract',
    triggerSelector: '.esign-trigger',
    loadingClass: 'esign-loading',
    errorClass: 'esign-error',
    timeout: 10000, // 10 seconds
  };

  // Email validation regex
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Initialize the SDK with optional configuration
   * @param {Object} options - Configuration options
   * @param {string} options.apiEndpoint - API endpoint URL
   * @param {string} options.triggerSelector - CSS selector for trigger buttons
   * @param {number} options.timeout - Request timeout in milliseconds
   */
  function init(options = {}) {
    Object.assign(config, options);

    // Auto-attach event listeners when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachListeners);
    } else {
      attachListeners();
    }
  }

  /**
   * Attach click listeners to all trigger elements
   */
  function attachListeners() {
    const triggers = document.querySelectorAll(config.triggerSelector);
    triggers.forEach(trigger => {
      // Avoid attaching multiple listeners
      if (trigger.dataset.esignAttached) return;
      trigger.dataset.esignAttached = 'true';

      trigger.addEventListener('click', handleTriggerClick);
    });
  }

  /**
   * Handle click on trigger element
   * @param {Event} event - Click event
   */
  async function handleTriggerClick(event) {
    event.preventDefault();

    const button = event.currentTarget;

    // Prevent double-clicks
    if (button.classList.contains(config.loadingClass)) {
      return;
    }

    // Clear any previous error state
    clearError(button);

    try {
      const signingUrl = await createContract(button);
      // Redirect to signing page
      window.location.href = signingUrl;
    } catch (error) {
      showError(button, error.message);
    }
  }

  /**
   * Extract data attributes from element
   * @param {HTMLElement} element - The trigger element
   * @returns {Object} Extracted data
   */
  function extractData(element) {
    const data = {
      template_id: element.dataset.templateId,
      document_name: element.dataset.documentName,
      signer_email: element.dataset.signerEmail,
      signer_name: element.dataset.signerName || null,
      redirect_url: element.dataset.redirectUrl || null,
      metadata: {},
    };

    // Extract metadata from data-metadata-* attributes
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.startsWith('metadata')) {
        // Convert 'metadataOrderId' to 'order_id'
        const metaKey = key
          .replace('metadata', '')
          .replace(/([A-Z])/g, '_$1')
          .toLowerCase()
          .replace(/^_/, '');
        if (metaKey) {
          data.metadata[metaKey] = value;
        }
      }
    }

    // Remove empty metadata object
    if (Object.keys(data.metadata).length === 0) {
      delete data.metadata;
    }

    return data;
  }

  /**
   * Validate extracted data
   * @param {Object} data - The data to validate
   * @returns {Object} Validation result { valid: boolean, errors: string[] }
   */
  function validate(data) {
    const errors = [];

    if (!data.template_id) {
      errors.push('Template ID is required (data-template-id)');
    }

    if (!data.document_name) {
      errors.push('Document name is required (data-document-name)');
    }

    if (!data.signer_email) {
      errors.push('Signer email is required (data-signer-email)');
    } else if (!EMAIL_REGEX.test(data.signer_email)) {
      errors.push('Invalid email format');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Create a contract and get the signing URL
   * @param {HTMLElement} element - The trigger element with data attributes
   * @returns {Promise<string>} The signing URL
   */
  async function createContract(element) {
    // Extract and validate data
    const data = extractData(element);
    const validation = validate(data);

    if (!validation.valid) {
      throw new Error(validation.errors.join('; '));
    }

    // Set loading state
    setLoading(element, true);

    try {
      // Make API request with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Parse response
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Request failed with status ${response.status}`);
      }

      if (!result.signing_url) {
        throw new Error('No signing URL returned from server');
      }

      return result.signing_url;

    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    } finally {
      setLoading(element, false);
    }
  }

  /**
   * Set loading state on element
   * @param {HTMLElement} element - The element
   * @param {boolean} loading - Whether loading
   */
  function setLoading(element, loading) {
    if (loading) {
      element.classList.add(config.loadingClass);
      element.dataset.originalText = element.textContent;
      element.textContent = 'Loading...';
      element.disabled = true;
    } else {
      element.classList.remove(config.loadingClass);
      if (element.dataset.originalText) {
        element.textContent = element.dataset.originalText;
        delete element.dataset.originalText;
      }
      element.disabled = false;
    }
  }

  /**
   * Show error message near element
   * @param {HTMLElement} element - The trigger element
   * @param {string} message - Error message
   */
  function showError(element, message) {
    element.classList.add(config.errorClass);

    // Create or update error message element
    let errorEl = element.nextElementSibling;
    if (!errorEl || !errorEl.classList.contains('esign-error-message')) {
      errorEl = document.createElement('div');
      errorEl.className = 'esign-error-message';
      errorEl.style.cssText = 'color: #dc3545; font-size: 0.875rem; margin-top: 0.5rem;';
      element.parentNode.insertBefore(errorEl, element.nextSibling);
    }
    errorEl.textContent = message;
  }

  /**
   * Clear error state from element
   * @param {HTMLElement} element - The trigger element
   */
  function clearError(element) {
    element.classList.remove(config.errorClass);

    const errorEl = element.nextElementSibling;
    if (errorEl && errorEl.classList.contains('esign-error-message')) {
      errorEl.remove();
    }
  }

  // Public API
  return {
    init,
    createContract,
    attachListeners,
    // Expose for testing
    _extractData: extractData,
    _validate: validate,
  };
})();

// Auto-initialize when script loads
ESignSDK.init();
